import {
  BrowserClient,
  defaultStackParser,
  makeFetchTransport,
  Scope,
  getDefaultIntegrations,
  User,
} from "@sentry/browser";
import { appConfig, servicesConfig, featureFlags } from "@/config/env";

// Only initialize Sentry if it's enabled
if (!featureFlags.enableSentry) {
  console.warn(
    "Sentry is disabled. Set VITE_ENABLE_SENTRY=true to enable error reporting."
  );
}

// Filter out integrations that rely on global state to avoid conflicts with external Sentry instances
const integrations = getDefaultIntegrations({}).filter((integration) => {
  return !["BrowserApiErrors", "Breadcrumbs", "GlobalHandlers"].includes(
    integration.name
  );
});

// Create an independent client using the SDK-specific DSN
const sdkClient = new BrowserClient({
  dsn: servicesConfig.sentry.dsn, // SDK's DSN (from config)
  transport: makeFetchTransport,
  stackParser: defaultStackParser,
  integrations, // Use only non-global integrations
  // Automatically set the environment from our config
  environment: appConfig.environment,
  enabled: featureFlags.enableSentry, // Only send events if Sentry is enabled
});

// Create an independent scope and bind the client
const sdkScope = new Scope();
sdkScope.setClient(sdkClient);

// Set global tags that will be sent with every error
sdkScope.setTag("app.version", appConfig.version);
sdkScope.setTag("app.name", appConfig.name);

// Initialize the client (must be called after setting the scope)
sdkClient.init();

/**
 * Captures an exception with the isolated Sentry client and scope.
 * @param error The error to capture.
 * @param tags Optional tags to add to this specific event.
 */
export const captureException = (
  error: any,
  others?: {
    tags?: { [key: string]: string };
    extra?: { [key: string]: any };
  }
) => {
  if (!featureFlags.enableSentry) {
    console.warn("Sentry is disabled. Error not sent:", error);
    return;
  }

  const { tags, extra } = others || {};

  // If there are temporary tags or extra info, clone the main scope to avoid race conditions
  if (
    (tags && Object.keys(tags).length > 0) ||
    (extra && Object.keys(extra).length > 0)
  ) {
    const tempScope = sdkScope.clone();

    if (tags) {
      tempScope.setTags(tags);
    }

    if (extra) {
      tempScope.setExtras(extra);
    }

    // captureException on a scope uses the client bound to that scope
    tempScope.captureException(error);
  } else {
    // Otherwise, use the default SDK scope
    sdkScope.captureException(error);
  }
};

/**
 * Sets the user information for Sentry error tracking.
 * @param user The user information to set.
 */
export const setUser = (user: User | null) => {
  if (!featureFlags.enableSentry) {
    console.warn("Sentry is disabled. User not set:", user);
    return;
  }

  sdkScope.setUser(user);
  console.log("User set for Sentry:", user?.id || "null");
};

/**
 * Clears the current user information from Sentry.
 */
export const clearUser = () => {
  if (!featureFlags.enableSentry) {
    console.warn("Sentry is disabled. User not cleared.");
    return;
  }

  sdkScope.setUser(null);
  console.log("User cleared from Sentry");
};
